import { useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Firebase Context
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { MembershipProvider } from './contexts/MembershipContext';

// Hooks
import { useScrollToTop } from './hooks/useScrollToTop';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTopButton from './components/ScrollToTopButton';
import ProtectedRoute from './components/auth/ProtectedRoute';
import FirebaseTest from './components/FirebaseTest';
import FirebaseConnectionTest from './components/FirebaseConnectionTest';
import DebugAuth from './components/DebugAuth';
import PaymentTest from './components/test/PaymentTest';
import DiscountCodeTest from './components/test/DiscountCodeTest';

// Auth Components
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import ForgotPassword from './pages/auth/ForgotPassword';

// Pages
import HomePage from './pages/HomePage';
import Dashboard from './pages/Dashboard';
import PendingApproval from './pages/PendingApproval';
import AdminDashboard from './pages/AdminDashboard';
import GroupsManagement from './pages/admin/GroupsManagement';
import GroupAdmin from './pages/admin/GroupAdmin';
import DataSeeder from './pages/admin/DataSeeder';
import UserManagement from './pages/admin/UserManagement';
import UserExport from './pages/admin/UserExport';
import SEOManagement from './pages/admin/SEOManagement';
import DebugGroups from './pages/admin/DebugGroups';
import TestFirebase from './pages/admin/TestFirebase';
import EventsMigration from './pages/admin/EventsMigration';
import EventCleanup from './pages/admin/EventCleanup';
import NewsMigration from './pages/admin/NewsMigration';
import NewsManagement from './pages/admin/NewsManagement';
import SiteTesting from './pages/admin/SiteTesting';
import CreateGrandReLaunch from './pages/admin/CreateGrandReLaunch';
import DatabaseCleanup from './pages/admin/DatabaseCleanup';
import PostingsDashboard from './pages/admin/PostingsDashboard';
import CampaignDashboard from './pages/admin/CampaignDashboard';
import ClientManagement from './pages/admin/ClientManagement';
import AgencyClients from './pages/admin/AgencyClients';
import AgencyPipeline from './pages/admin/AgencyPipeline';
import ClientPortal from './pages/admin/ClientPortal';
import LinkedInGroups from './components/linkedin/LinkedInGroups';
import GraduateSourcing from './components/linkedin/GraduateSourcing';

// Form Components
import JobForm from './components/jobs/JobForm';
import ProjectForm from './components/projects/ProjectForm';
import EventForm from './components/events/EventForm';
import AdminSetupGuide from './components/AdminSetupGuide';

// Client Portal Pages
import ClientLogin from './pages/client/ClientLogin';
import ClientDashboard from './pages/client/ClientDashboard';
import ClientDeposit from './pages/client/ClientDeposit';
import ClientDepositSuccess from './pages/client/ClientDepositSuccess';

// About Pages
import OurStory from './pages/about/OurStory';
import MembershipInfo from './pages/about/MembershipInfo';
import MembershipSelection from './pages/MembershipSelection';
import MembershipRegistration from './pages/MembershipRegistration';
import Membership from './pages/Membership';
import MembershipPayment from './pages/MembershipPayment';
import MembershipSuccess from './pages/MembershipSuccess';
import MembershipCancel from './pages/MembershipCancel';
import MembershipRenew from './pages/MembershipRenew';
import ProfileCompletion from './pages/ProfileCompletion';
import Sponsorship from './pages/about/Sponsorship';
import AdditionalServices from './pages/about/AdditionalServices';
import ContactUs from './pages/about/ContactUs';

// SEO Landing Pages
import AcceleratedProfessionalsPage from './pages/seo/AcceleratedProfessionalsPage';
import ProfessionalNetworkPage from './pages/seo/ProfessionalNetworkPage';

// Legal Pages
import TermsAndConditions from './pages/legal/TermsAndConditions';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';

// Main Pages
import Events from './pages/Events';
import EventDetail from './pages/EventDetail';
import RegistrationSuccess from './pages/events/RegistrationSuccess';
import RegistrationCancel from './pages/events/RegistrationCancel';
import News from './pages/News';
import NewsDetail from './pages/NewsDetail';



// Member Pages
import MemberDirectory from './pages/members/MemberDirectory';
import MemberProfile from './pages/members/MemberProfile';
import Groups from './pages/members/Groups';
import GroupDetail from './pages/members/GroupDetail';
import GroupCreate from './pages/members/GroupCreate';
import JobBoard from './pages/members/JobBoard';
import JobDetail from './pages/members/JobDetail';
import MyPostings from './pages/members/MyPostings';
import Messages from './pages/members/Messages';
import ProjectBoard from './pages/members/ProjectBoard';
import ProjectDetail from './pages/members/ProjectDetail';
import PostProject from './pages/members/PostProject';
import PostJob from './pages/members/PostJob';
import Profile from './pages/members/Profile';
import MyNetwork from './pages/members/MyNetwork';

function App() {
  const headerRef = useRef(null);

  return (
    <AuthProvider>
      <MembershipProvider>
        <Router>
          <AppContent headerRef={headerRef} />
        </Router>
      </MembershipProvider>
    </AuthProvider>
  );
}

function AppContent({ headerRef }) {
  const { currentUser, logout } = useAuth();

  // Scroll to top on route changes
  useScrollToTop();

  // Check if current route should have header
  const noHeaderRoutes = ['/login', '/forgot-password', '/pending-approval', '/firebase-connection', '/firebase-test', '/debug-auth', '/admin-setup', '/client/login', '/client/dashboard', '/client/deposit'];
  const currentPath = window.location.pathname;
  const shouldShowHeader = !noHeaderRoutes.some(route => currentPath.startsWith(route));

  return (
    <div className="flex flex-col min-h-screen">
      {shouldShowHeader && (
        <Header
          ref={headerRef}
          isLoggedIn={!!currentUser}
          onLogout={logout}
        />
      )}
      <main className="flex-grow">
        <Routes>
          {/* Auth Routes - No header */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Navigate to="/register" replace />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/pending-approval" element={<PendingApproval />} />

          {/* Firebase Test Routes - For development */}
          <Route path="/firebase-connection" element={<FirebaseConnectionTest />} />
          <Route path="/firebase-test" element={
            <ProtectedRoute>
              <FirebaseTest />
            </ProtectedRoute>
          } />
          <Route path="/debug-auth" element={<DebugAuth />} />
          <Route path="/admin-setup" element={<AdminSetupGuide />} />
          <Route path="/payment-test" element={<PaymentTest />} />

          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />

          {/* SEO Landing Pages - Public for maximum visibility */}
          <Route path="/accelerated-professionals" element={<AcceleratedProfessionalsPage />} />
          <Route path="/professional-network" element={<ProfessionalNetworkPage />} />
          <Route path="/accelerated" element={<AcceleratedProfessionalsPage />} />
          <Route path="/professional" element={<ProfessionalNetworkPage />} />
          <Route path="/network" element={<ProfessionalNetworkPage />} />

          {/* About Pages */}
          <Route path="/about/our-story" element={<OurStory />} />
          <Route path="/about/membership" element={<MembershipInfo />} />
          <Route path="/membership" element={<Navigate to="/register" replace />} />
          <Route path="/register" element={<MembershipRegistration />} />
          <Route path="/membership-payment" element={<MembershipPayment />} />
          <Route path="/membership/success" element={<MembershipSuccess />} />
          <Route path="/membership/cancel" element={<MembershipCancel />} />
          <Route path="/membership/renew" element={
            <ProtectedRoute>
              <MembershipRenew />
            </ProtectedRoute>
          } />
          <Route path="/profile/complete" element={<ProfileCompletion />} />
          <Route path="/membership/legacy" element={<MembershipSelection />} />
          <Route path="/about/sponsorship" element={<Sponsorship />} />
          <Route path="/about/additional-services" element={<AdditionalServices />} />
          <Route path="/about/contact" element={<ContactUs />} />

          {/* Legal Pages */}
          <Route path="/legal/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />

          {/* Main Pages */}
          <Route path="/events" element={<Events />} />
          <Route path="/events/:id" element={<EventDetail />} />
          <Route path="/events/:id/registration/success" element={<RegistrationSuccess />} />
          <Route path="/events/:id/registration/cancel" element={<RegistrationCancel />} />
          <Route path="/news" element={<News />} />
          <Route path="/news/:id" element={<NewsDetail />} />

          {/* Client Portal Routes (session-based access codes, no Firebase Auth) */}
          <Route path="/client/login" element={<ClientLogin />} />
          <Route path="/client/dashboard" element={<ClientDashboard />} />
          <Route path="/client/deposit/:id" element={<ClientDeposit />} />
          <Route path="/client/deposit/:id/success" element={<ClientDepositSuccess />} />

          {/* Protected Member Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute requireApproval={true} requireActiveMembership={true}>
              <Dashboard />
            </ProtectedRoute>
          } />

          <Route path="/directory" element={
            <ProtectedRoute requireApproval={true} requireActiveMembership={true}>
              <MemberDirectory />
            </ProtectedRoute>
          } />

          <Route path="/directory/:id" element={
            <ProtectedRoute requireApproval={true} requireActiveMembership={true}>
              <MemberProfile />
            </ProtectedRoute>
          } />

          <Route path="/groups" element={
            <ProtectedRoute requireApproval={true} requireActiveMembership={true}>
              <Groups />
            </ProtectedRoute>
          } />

          <Route path="/groups/create" element={
            <ProtectedRoute requireApproval={true} requireActiveMembership={true}>
              <GroupCreate />
            </ProtectedRoute>
          } />

          <Route path="/groups/:id" element={
            <ProtectedRoute requireApproval={true} requireActiveMembership={true}>
              <GroupDetail />
            </ProtectedRoute>
          } />

          {/* Not adminOnly: group creators/moderators manage their own groups here.
              The GroupAdmin component itself gates on isAdmin || isGroupModerator
              (and shows "Access Denied" to anyone else). */}
          <Route path="/groups/:id/admin" element={
            <ProtectedRoute requireApproval={true} requireActiveMembership={true}>
              <GroupAdmin />
            </ProtectedRoute>
          } />

          <Route path="/jobs" element={
            <ProtectedRoute requireApproval={true} requireActiveMembership={true}>
              <JobBoard />
            </ProtectedRoute>
          } />

          <Route path="/my-postings" element={
            <ProtectedRoute requireApproval={true} requireActiveMembership={true}>
              <MyPostings />
            </ProtectedRoute>
          } />

          <Route path="/messages" element={
            <ProtectedRoute requireApproval={true} requireActiveMembership={true}>
              <Messages />
            </ProtectedRoute>
          } />

          <Route path="/messages/:conversationId" element={
            <ProtectedRoute requireApproval={true} requireActiveMembership={true}>
              <Messages />
            </ProtectedRoute>
          } />

                    <Route path="/jobs/:id" element={
                      <ProtectedRoute requireApproval={true} requireActiveMembership={true}>
                        <JobDetail />
                      </ProtectedRoute>
                    } />

                    <Route path="/jobs/post" element={
                      <ProtectedRoute requireApproval={true} requireActiveMembership={true}>
                        <PostJob />
                      </ProtectedRoute>
                    } />

                    <Route path="/projects" element={
                      <ProtectedRoute requireApproval={true} requireActiveMembership={true}>
                        <ProjectBoard />
                      </ProtectedRoute>
                    } />

                    <Route path="/projects/:id" element={
                      <ProtectedRoute requireApproval={true} requireActiveMembership={true}>
                        <ProjectDetail />
                      </ProtectedRoute>
                    } />

                    <Route path="/projects/post" element={
                      <ProtectedRoute requireApproval={true} requireActiveMembership={true}>
                        <PostProject />
                      </ProtectedRoute>
                    } />

                    <Route path="/profile" element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    } />

                    <Route path="/my-network" element={
                      <ProtectedRoute requireApproval={true} requireActiveMembership={true}>
                        <MyNetwork />
                      </ProtectedRoute>
                    } />

                    {/* Admin Routes */}
                    <Route path="/admin" element={
                      <ProtectedRoute adminOnly={true}>
                        <AdminDashboard />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/user-management" element={
                      <ProtectedRoute adminOnly={true}>
                        <AdminDashboard />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/events/create" element={
                      <ProtectedRoute adminOnly={true}>
                        <EventForm />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/events/edit/:id" element={
                      <ProtectedRoute adminOnly={true}>
                        <EventForm />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/groups" element={
                      <ProtectedRoute adminOnly={true}>
                        <GroupsManagement />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/postings" element={
                      <ProtectedRoute adminOnly={true}>
                        <PostingsDashboard />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/users" element={
                      <ProtectedRoute adminOnly={true}>
                        <UserManagement />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/seo" element={
                      <ProtectedRoute adminOnly={true}>
                        <SEOManagement />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/export" element={
                      <ProtectedRoute adminOnly={true}>
                        <UserExport />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/seed-data" element={
                      <ProtectedRoute adminOnly={true}>
                        <DataSeeder />
                      </ProtectedRoute>
                    } />


                    <Route path="/admin/debug-groups" element={
                      <ProtectedRoute adminOnly={true}>
                        <DebugGroups />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/test-firebase" element={
                      <ProtectedRoute adminOnly={true}>
                        <TestFirebase />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/migrate-events" element={
                      <ProtectedRoute adminOnly={true}>
                        <EventsMigration />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/event-cleanup" element={
                      <ProtectedRoute adminOnly={true}>
                        <EventCleanup />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/create-grand-relaunch" element={
                      <ProtectedRoute adminOnly={true}>
                        <CreateGrandReLaunch />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/database-cleanup" element={
                      <ProtectedRoute adminOnly={true}>
                        <DatabaseCleanup />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/migrate-news" element={
                      <ProtectedRoute adminOnly={true}>
                        <NewsMigration />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/news-management" element={
                      <ProtectedRoute adminOnly={true}>
                        <NewsManagement />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/testing" element={
                      <ProtectedRoute adminOnly={true}>
                        <SiteTesting />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/campaigns" element={
                      <ProtectedRoute adminOnly={true}>
                        <CampaignDashboard />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/clients" element={
                      <ProtectedRoute adminOnly={true}>
                        <ClientManagement />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/agency-clients" element={
                      <ProtectedRoute adminOnly={true}>
                        <AgencyClients />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/agency-pipeline" element={
                      <ProtectedRoute adminOnly={true}>
                        <AgencyPipeline />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/client-portal" element={
                      <ProtectedRoute adminOnly={true}>
                        <ClientPortal />
                      </ProtectedRoute>
                    } />

                    {/* Form Routes */}
                    <Route path="/jobs/create" element={
                      <ProtectedRoute requireApproval={true} requireActiveMembership={true}>
                        <JobForm />
                      </ProtectedRoute>
                    } />

                    <Route path="/projects/create" element={
                      <ProtectedRoute requireApproval={true} requireActiveMembership={true}>
                        <ProjectForm />
                      </ProtectedRoute>
                    } />

                    {/* LinkedIn Integration Routes - Admin Only */}
                    <Route path="/linkedin/groups" element={
                      <ProtectedRoute requireRole="admin">
                        <LinkedInGroups />
                      </ProtectedRoute>
                    } />

                    <Route path="/linkedin/graduates" element={
                      <ProtectedRoute requireRole="admin">
                        <GraduateSourcing />
                      </ProtectedRoute>
                    } />

                    {/* Test Routes */}
                    <Route path="/test/discount" element={<DiscountCodeTest />} />

                    {/* Catch-all: any unmatched URL (typos, dead links) goes home
                        instead of rendering a blank white screen. */}
                    <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />

      {/* Scroll to Top Button */}
      <ScrollToTopButton />
    </div>
  );
}

export default App;
